/**
 * Module-level snapshot of the latest permissions payload returned by
 * `GET /api/me/permissions`.
 *
 * Why not just React state? Most role-gate call-sites in this app
 * still use the plain, non-hook `hasPermission(currentUser, [perm])`
 * signature that predates any provider. Migrating every single one
 * atomically is high-risk churn; instead the provider mirrors its
 * latest fetch into this module cache, and `hasPermission()`
 * consults the cache first (source = 'api') and only falls back to
 * the legacy JWT/ROLE_PERMISSIONS mapping when the endpoint hasn't
 * responded yet OR isn't deployed (source = 'jwt').
 *
 * This is a plain object — not a store — because the value only
 * changes on user login / logout / manual refetch, and the provider
 * bumps context state at the same time so components subscribed via
 * `useCurrentPermissions` / `useHasPermission` re-render normally.
 * Non-hook readers pick up the new value on their next render.
 *
 * SCK-547.
 */

let snapshot = {
  source: null, // 'api' | 'jwt' | null
  permissions: [], // string[] granted verbs
  projects: [], // [{ projectId, projectName, role }]
  accountRole: null, // 'ACCOUNT_ADMIN' | 'MEMBER' | ...
};

export const setPermissionsSnapshot = (next) => {
  snapshot = { ...snapshot, ...next };
};

export const getPermissionsSnapshot = () => snapshot;

export const clearPermissionsSnapshot = () => {
  snapshot = {
    source: null,
    permissions: [],
    projects: [],
    accountRole: null,
  };
};
